import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { attemptFirestoreQuotaSelfHeal, isFirestoreQuotaCrash } from '../utils/errorHandlers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const ErrorContainer = styled.div`
  padding: 20px;
  margin: 20px 0;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  background-color: #f8d7da;
`;

const ErrorHeading = styled.h3`
  color: #721c24;
  margin-top: 0;
`;

const ErrorMessage = styled.p`
  margin: 10px 0;
`;

const ErrorStack = styled.pre`
  background-color: rgba(0, 0, 0, 0.05);
  padding: 10px;
  border-radius: 4px;
  overflow: auto;
  font-size: 0.8em;
`;

const RetryButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  font-weight: bold;

  &:hover {
    background-color: #c82333;
  }
`;

// Chunk-failure card: calm cream/ink (the splash overlay palette), never
// the red wall — nothing on the page is broken beyond a stale bundle.
const RefreshContainer = styled.div`
  padding: 24px;
  margin: 20px 0;
  border: 1px solid #e8e3d9;
  border-radius: 8px;
  color: #1a1a1a;
  background-color: #fafaf8;
  text-align: center;
`;

const RefreshHeading = styled.h3`
  color: #1a1a1a;
  margin-top: 0;
`;

const ReloadButton = styled.button`
  background-color: #1a1a1a;
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 10px;
  font-weight: 600;

  &:hover {
    background-color: #333;
  }
`;

// The card for a tab whose Firestore SDK has died (2026-09-28, Malaika's
// freezes). Shown when the automatic heal declined to reload (it reloads at
// most once per five minutes); the button runs the heal regardless, because
// a person's click cannot loop.
// copy: firestore_refresh_card
export const FIRESTORE_REFRESH_CARD_COPY = {
  heading: 'prest0 needs a quick refresh',
  body: 'the connection to your workspace stopped responding. one refresh brings you back right where you were.',
  button: 'refresh',
} as const;

/**
 * Error boundary component to catch JavaScript errors in child components
 * and display a fallback UI instead of crashing the entire application
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    // Firestore localStorage-quota crash (INCIDENT 2026-08-22): the SDK's
    // internal assertion usually reaches the user AS this boundary — a red
    // wall nothing on the page can fix. Make room, mark this tab to start on
    // the in-memory cache, and reload once (sessionStorage-guarded, so a heal
    // that doesn't stick falls back to the calm refresh card below instead of
    // reload-looping).
    if (isFirestoreQuotaCrash(error)) {
      attemptFirestoreQuotaSelfHeal({ cause: error });
    }

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Check if a custom fallback was provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Chunk failures get a reload-first card: React.lazy caches the
      // rejected factory for the module lifetime, so the default card's
      // "Try Again" boundary reset would just re-throw the same error —
      // only a page reload can revive the route. lazyWithRetry marks its
      // exhausted errors (__prest0ChunkRecoveryExhausted); name/message
      // matching covers chunk errors that reached us some other way.
      const err = this.state.error as (Error & { __prest0ChunkRecoveryExhausted?: boolean }) | null;
      if (err && isFirestoreQuotaCrash(err)) {
        return (
          <RefreshContainer role="alert" data-reason="firestore-stopped">
            <RefreshHeading>{FIRESTORE_REFRESH_CARD_COPY.heading}</RefreshHeading>
            <ErrorMessage>{FIRESTORE_REFRESH_CARD_COPY.body}</ErrorMessage>
            <ReloadButton
              onClick={() => {
                if (!attemptFirestoreQuotaSelfHeal({ force: true })) window.location.reload();
              }}
            >
              {FIRESTORE_REFRESH_CARD_COPY.button}
            </ReloadButton>
          </RefreshContainer>
        );
      }
      const isChunkFailure =
        !!err &&
        (err.name === 'ChunkLoadError' ||
          err.__prest0ChunkRecoveryExhausted === true ||
          /Loading chunk/.test(err.message || ''));
      if (isChunkFailure) {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        return (
          <RefreshContainer role="alert" data-reason="chunk-failure">
            <RefreshHeading>prest0 needs a quick refresh</RefreshHeading>
            <ErrorMessage>
              this page picked up an update while you were away. one reload brings
              you back right where you were.
              {offline ? ' you look offline — reconnect first, then reload.' : ''}
            </ErrorMessage>
            <ReloadButton onClick={() => window.location.reload()}>
              reload
            </ReloadButton>
          </RefreshContainer>
        );
      }

      // Render default fallback UI. The raw message + stack live behind a
      // collapsed details toggle (2026-08-19 owner report: a Firestore
      // internal assertion painted a full-width red wall of minified stack
      // as the whole app) — the headline stays calm and human, the
      // technical detail stays one click away for support.
      return (
        <ErrorContainer>
          <ErrorHeading>Something went wrong</ErrorHeading>
          <ErrorMessage>
            This part of the app hit an unexpected error. Your data is safe —
            try again, or reload the page if it keeps happening.
          </ErrorMessage>
          <RetryButton onClick={this.resetErrorBoundary}>
            Try Again
          </RetryButton>
          {this.state.error && (
            <details style={{ marginTop: '12px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.85em', opacity: 0.75 }}>
                technical details (for support)
              </summary>
              <ErrorMessage>{this.state.error.message || 'An unexpected error occurred'}</ErrorMessage>
              <ErrorStack>{this.state.error.stack}</ErrorStack>
            </details>
          )}
        </ErrorContainer>
      );
    }

    // When there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary; 